'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FilterLockBadge, useLockHint } from './FilterLockBadge';

/**
 * The company count for one filter option, right-aligned after its label.
 *
 * `undefined` renders nothing — that is "we don't know yet" (facets still
 * loading, or a DB without migration 305), which must look like the old UI
 * rather than like zero. An actual 0 is rendered, because "this option
 * matches nothing" is exactly what the user needs to see before clicking it.
 */
export function FacetCount({ count }: { count?: number }) {
  const { i18n } = useTranslation();
  const fmt = useMemo(
    () => new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language || 'en'),
    [i18n.resolvedLanguage, i18n.language],
  );
  if (count === undefined) return null;
  return (
    <span
      className={cn(
        'ml-auto pl-2 shrink-0 text-[10px] tabular-nums',
        count === 0 ? 'text-muted-foreground/50' : 'text-muted-foreground',
      )}
    >
      {fmt.format(count)}
    </span>
  );
}

/**
 * Label for a filter the visitor cannot use yet, with the chip that says why.
 *
 * Renders the plain label when `lock` is null, so a caller can wrap every
 * field unconditionally and the gating stays in one place. The whole row is
 * the affordance: `title` carries the explanation for pointer users, and the
 * chip text carries it for everyone else.
 */
export function FilterLabel({
  lock,
  className,
  children,
}: {
  lock: 'user' | 'pro' | null;
  className?: string;
  children: React.ReactNode;
}) {
  const lockHint = useLockHint();
  if (!lock) return <Label className={className}>{children}</Label>;

  // `flex-wrap` + `min-w-0` so a long German label ("Personen (Geschäfts-
  // führer, Gesellschafter, Prokurist…)") pushes the chip onto its own line
  // on a phone instead of off the edge of the panel.
  return (
    <div className="flex flex-wrap items-center gap-1.5" title={lockHint(lock)}>
      <Label className={cn(className, 'min-w-0 text-muted-foreground')}>
        {children}
      </Label>
      <FilterLockBadge lock={lock} />
    </div>
  );
}

/**
 * A checkbox row whose label may carry a lock chip.
 *
 * Separate from `FilterLabel` because shadcn's `Label` renders a `<label>`
 * element, and these rows are already wrapped in one — nesting them is
 * invalid HTML and breaks the click-to-toggle behaviour.
 */
export function FilterCheckbox({
  checked,
  onCheckedChange,
  lock,
  label,
  count,
  hint,
  role,
}: {
  role?: 'radio';
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  lock: 'user' | 'pro' | null;
  label: string;
  count?: number;
  /**
   * One-sentence explanation shown on hover. For derived filters whose
   * label cannot carry its own definition — "Inhabergeführt" is a claim
   * about the register, not a self-evident category.
   *
   * A lock takes precedence: the native `title` explaining *why the
   * control is disabled* is the more useful message to a visitor who
   * cannot use it at all.
   */
  hint?: string;
}) {
  const lockHint = useLockHint();
  const row = (
    <label
      title={lock ? lockHint(lock) : undefined}
      className={cn(
        'flex flex-wrap items-center gap-2 select-none min-h-11 lg:min-h-9',
        lock ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      <Checkbox
        role={role}
        checked={checked}
        disabled={!!lock}
        onCheckedChange={(v) => onCheckedChange(v === true)}
      />
      <span className={cn(FIELD_LABEL_CLASS, lock && 'text-muted-foreground')}>
        {label}
      </span>
      {lock && <FilterLockBadge lock={lock} />}
      <FacetCount count={count} />
    </label>
  );

  if (!hint || lock) return row;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{row}</TooltipTrigger>
        <TooltipContent side="right" align="start" className="max-w-[19rem] text-xs">
          {hint}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const FIELD_LABEL_CLASS = 'text-xs text-foreground font-medium';
export const FIELD_PLACEHOLDER_CLASS =
  'text-[0.8125rem] leading-(--text-sm--line-height) text-muted-foreground/80';
export const INPUT_PLACEHOLDER_CLASS = 'placeholder:text-muted-foreground/80';

